/*
 * PROD-7828 D111 — Close versions tab: view state, its URL form and the state transitions. Pure; the
 * wire builders are in requests.js. Values mirror close_versions_enums.json (pinned by params.test.js).
 */
import { build_query_string } from '../overview/params';

export const MODE_REOPENED_NOW = 'REOPENED_NOW';
export const MODE_CLOSE_VERSIONS = 'CLOSE_VERSIONS';
export const MODES = [MODE_REOPENED_NOW, MODE_CLOSE_VERSIONS];

export const SORT_KEYS = [
  'abs_net_diff',
  'net_diff',
  'teacher_name',
  'baseline_net',
  'current_net',
  'version_count',
  'changed_at',
  'status',
  'reopen_reason'
];
export const DEFAULT_SORT = 'abs_net_diff';
export const DEFAULT_DIR = 'desc';
const DIRS = ['asc', 'desc'];

export const PAGE_SIZE = 100;
export const QUERY_MAX_LEN = 100;
export const QUERY_TOO_LONG_TEXT = `Search is limited to ${QUERY_MAX_LEN} characters.`;

// The server validates the uuid itself; this only keeps a typed URL from sending arbitrary text.
const TEACHER_UUID_RE = /^[A-Za-z0-9-]{1,64}$/;

export const TAB_PATH = '/teacher-payments/close-versions';

const in_list = (list, v) => list.indexOf(v) !== -1;
const bool_param = (v) => (v === 'true' ? true : v === 'false' ? false : null);

// multi_version null = the mode's default (C5: on in Close versions); Reopened now never sends true.
export const blank_state = () => ({
  mode: '',
  only_changed: false,
  multi_version: null,
  q: '',
  sort: DEFAULT_SORT,
  dir: DEFAULT_DIR,
  offset: 0,
  teacher_uuid: ''
});

/*
 * The URL's state for pay_month. mode and teacher_uuid belong to the month they were written for, so a
 * URL from another month keeps only the filters and sort (the month picker moved on).
 */
export const parse_state = (search, pay_month) => {
  const qs = new URLSearchParams(typeof search === 'string' ? search : '');
  const get = (k) => (qs.get(k) || '').trim();
  const link_month = get('pay_month');
  const same_month = !link_month || link_month === pay_month;
  const offset = Number(get('offset'));
  const q = get('q');
  const s = blank_state();
  return {
    ...s,
    mode: same_month && in_list(MODES, get('mode')) ? get('mode') : '',
    only_changed: bool_param(get('only_changed')) === true,
    multi_version: bool_param(get('multi_version')),
    q: q.length <= QUERY_MAX_LEN ? q : '',
    sort: in_list(SORT_KEYS, get('sort')) ? get('sort') : s.sort,
    dir: in_list(DIRS, get('dir')) ? get('dir') : s.dir,
    offset:
      same_month && Number.isInteger(offset) && offset > 0 && offset % PAGE_SIZE === 0 ? offset : 0,
    teacher_uuid: same_month && TEACHER_UUID_RE.test(get('teacher_uuid')) ? get('teacher_uuid') : ''
  };
};

// Kuldeep D111: an explicit mode (URL or a click) wins; else the server's default_mode.
export const effective_mode = (state, summary) => {
  if (in_list(MODES, state.mode)) return state.mode;
  const d = summary && summary.default_mode;
  return in_list(MODES, d) ? d : '';
};

export const multi_version_on = (state, mode) =>
  mode === MODE_CLOSE_VERSIONS && state.multi_version !== false;

// Switching resets the page and the mode-specific filter, and keeps the sort (brief §13).
export const with_mode = (state, mode) => ({ ...state, mode, multi_version: null, offset: 0 });

export const with_filter = (state, patch) => ({ ...state, ...patch, offset: 0 });

// The same key flips the direction; a new key starts on its natural order (names A-Z, amounts biggest first).
export const with_sort = (state, key) => {
  if (!in_list(SORT_KEYS, key)) return state;
  if (state.sort === key) {
    return { ...state, dir: state.dir === 'asc' ? 'desc' : 'asc', offset: 0 };
  }
  return { ...state, sort: key, dir: key === 'teacher_name' ? 'asc' : 'desc', offset: 0 };
};

export const has_filters = (state, mode) =>
  state.only_changed ||
  state.q !== '' ||
  (mode === MODE_CLOSE_VERSIONS && state.multi_version === false);

// "Clear filters" returns to the mode's defaults, multi_version included.
export const clear_filters = (state) => ({
  ...state,
  only_changed: false,
  multi_version: null,
  q: '',
  offset: 0
});

const STATE_SEARCH_KEYS = [
  'pay_month',
  'mode',
  'only_changed',
  'multi_version',
  'q',
  'sort',
  'dir',
  'offset',
  'teacher_uuid'
];

// The URL the tab writes back: only what differs from the defaults, plus the month it belongs to -
// merged into the current search, so the shell's own ?month= (or any other param) survives.
export const state_search = (search, state, pay_month) => {
  const s = blank_state();
  const own = {
    pay_month,
    mode: state.mode,
    only_changed: state.only_changed ? 'true' : '',
    multi_version: state.multi_version === null ? '' : String(state.multi_version),
    q: state.q,
    sort: state.sort !== s.sort ? state.sort : '',
    dir: state.dir !== s.dir ? state.dir : '',
    offset: state.offset > 0 ? state.offset : '',
    teacher_uuid: state.teacher_uuid
  };
  const qs = new URLSearchParams(typeof search === 'string' ? search : '');
  STATE_SEARCH_KEYS.forEach((k) => qs.delete(k));
  STATE_SEARCH_KEYS.forEach((k) => {
    if (own[k]) qs.set(k, own[k]);
  });
  return qs.toString();
};

// Overview's "Versions" link: the tab opens with the teacher-month's versions dialog over the list.
export const versions_link = (pay_month, teacher_uuid) =>
  `${TAB_PATH}?${build_query_string({ pay_month, teacher_uuid })}`;

// C10: every teacher-month closed now or closed before (reopen_count > 0).
export const shows_versions_link = (row) =>
  !!row && (row.lifecycle === 'CLOSED' || Number(row.reopen_count) > 0);
